/**
 * Where the fleet is.
 *
 * ── A basemap, and still no tiles ────────────────────────────────────────
 *
 * 🔴 This screen refused a basemap for most of its life, and the argument was
 * half right. It ran: every mapping library pulls tiles from somebody else's
 * server, this platform watches sites on 4G behind a VPN, and a screen that
 * renders blank without internet is blank exactly when an operator needs it.
 *
 * All of that is true of TILE SERVERS. None of it is an argument against a map.
 * The conclusion drawn from it — "so there is no basemap" — cost every operator
 * the ability to tell where anything was, permanently, in order to avoid a
 * dependency that could simply not be taken.
 *
 * So the geometry is bundled. `basemap-au.ts` carries Australia's coastline and
 * state borders as coordinates inside the JavaScript, from Natural Earth, which
 * is public domain. No runtime request, no third party, nothing to be blank —
 * and the VPN-only operator the old argument was written to protect now gets a
 * map instead of a scatter plot.
 *
 * ⚠️ What it is not: 1:50m data rounded to three decimals, so it places a yard
 * within a region rather than anything survey-grade. Coordinates are still on
 * every row, and are still the thing to paste into a real map when the question
 * is metres rather than which side of the state a site is on.
 *
 * ── Two sources of position, never merged ────────────────────────────────
 *
 * A router's own fix and the site somebody typed are different claims. The
 * server says which one each marker came from and this screen keeps them
 * visually distinct, because reading "the yard this belongs to" as "where this
 * router says it is" is exactly how a stolen device looks fine on a map.
 *
 * ── Drift is the finding ─────────────────────────────────────────────────
 *
 * An asset far from its filed site has moved, was filed wrong, or is in
 * somebody's van. Nothing in the predecessor could notice any of those, because
 * it never held both numbers at once.
 */

import type React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { ApiError, api } from './api.ts';
import { AU_BOUNDS, AU_STATES } from './basemap-au.ts';
import {
  Banner,
  Button,
  DataTable,
  Empty,
  Loading,
  Pill,
  Section,
  type ColumnDef,
} from './components/index.ts';

interface MapAsset {
  id: string;
  name: string;
  code: string | null;
  kind: string;
  site: { id: string | null; name: string | null; code: string | null };
  latitude: number | null;
  longitude: number | null;
  source: 'device' | 'site' | 'none';
  driftM: number | null;
  drifted: boolean;
  fixAt: string | null;
}

interface MapResponse {
  assets: MapAsset[];
  located: number;
  drifted: number;
}

/** Padding inside the plot, in viewBox units, so edge markers are not clipped. */
const PAD = 24;
const VIEW_W = 720;
/*
 * Sized for the subject rather than for the box. Australia spans about 40.7
 * degrees of longitude and 32.9 of latitude, and once longitude is corrected
 * for latitude that is close to 1.1:1 — so the old 720x420 viewBox would
 * letterbox the continent between two wide empty margins.
 */
const VIEW_H = 640;

type Bounds = readonly [number, number, number, number];

interface Projection {
  x: (lon: number) => number;
  y: (lat: number) => number;
}

/**
 * One projection, used by the coastline and the markers alike.
 *
 * 🔴 Shared rather than computed twice. Two projections that agree today would
 * drift apart the first time either was adjusted, and a fleet map whose markers
 * sit beside the coast rather than on it is worse than no coastline at all —
 * it looks authoritative while being wrong.
 *
 * Longitude is scaled by cos(latitude) so the aspect ratio is not a lie: at
 * Australian latitudes a degree of longitude is roughly seven-eighths of a
 * degree of latitude, and plotting them 1:1 stretches everything east-west.
 */
function projectorFor(bounds: Bounds): Projection {
  const [west, south, east, north] = bounds;
  const midLat = (south + north) / 2;
  const lonScale = Math.max(0.1, Math.cos((midLat * Math.PI) / 180));

  const spanLon = Math.max(1e-6, (east - west) * lonScale);
  const spanLat = Math.max(1e-6, north - south);

  const w = VIEW_W - PAD * 2;
  const h = VIEW_H - PAD * 2;
  const scale = Math.min(w / spanLon, h / spanLat);

  // Centre whatever the tighter axis leaves over.
  const offsetX = (w - spanLon * scale) / 2;
  const offsetY = (h - spanLat * scale) / 2;

  return {
    x: (lon) => PAD + offsetX + (lon - west) * lonScale * scale,
    // Latitude increases northward and y increases downward.
    y: (lat) => PAD + offsetY + (north - lat) * scale,
  };
}

interface Placed extends MapAsset {
  x: number;
  y: number;
}

function locatedOnly(
  assets: readonly MapAsset[],
): (MapAsset & { latitude: number; longitude: number })[] {
  return assets.filter(
    (a): a is MapAsset & { latitude: number; longitude: number } =>
      a.latitude !== null && a.longitude !== null,
  );
}

/**
 * The box the fleet occupies, with a margin so nothing sits on the frame.
 *
 * ⚠️ A minimum span, because a single asset — or several at one yard — has no
 * extent, and a projection fitted to a zero-width box magnifies rounding error
 * until the coastline is a straight line across the middle of the screen. Half
 * a degree is about 55 km, which is a sensible smallest view.
 */
function fleetBounds(assets: readonly MapAsset[]): Bounds | null {
  const located = locatedOnly(assets);
  if (located.length === 0) return null;

  const lats = located.map((a) => a.latitude);
  const lons = located.map((a) => a.longitude);
  const pad = 0.25;
  const west = Math.min(...lons) - pad;
  const east = Math.max(...lons) + pad;
  const south = Math.min(...lats) - pad;
  const north = Math.max(...lats) + pad;

  const minSpan = 0.5;
  const growX = Math.max(0, (minSpan - (east - west)) / 2);
  const growY = Math.max(0, (minSpan - (north - south)) / 2);
  return [west - growX, south - growY, east + growX, north + growY];
}

function place(assets: readonly MapAsset[], p: Projection): Placed[] {
  return locatedOnly(assets).map((a) => ({
    ...a,
    x: p.x(a.longitude),
    y: p.y(a.latitude),
  }));
}

/** One ring as an SVG path. */
function ringPath(ring: readonly (readonly [number, number])[], p: Projection): string {
  let d = '';
  for (let i = 0; i < ring.length; i++) {
    const point = ring[i]!;
    d += `${i === 0 ? 'M' : 'L'}${p.x(point[0]).toFixed(1)} ${p.y(point[1]).toFixed(1)}`;
  }
  return `${d}Z`;
}

function metres(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`;
}

const COLUMNS: ColumnDef<MapAsset, unknown>[] = [
  {
    id: 'asset',
    header: 'Asset',
    accessorFn: (a) => a.code ?? a.name,
    cell: ({ row }) => (
      <>
        {row.original.name}
        {row.original.code === null ? null : <span className="muted"> {row.original.code}</span>}
      </>
    ),
  },
  {
    id: 'site',
    header: 'Site',
    accessorFn: (a) => a.site.name ?? '',
    cell: ({ getValue }) => (getValue() as string) || <span className="muted">—</span>,
  },
  {
    id: 'position',
    header: 'Position',
    accessorFn: (a) => (a.latitude === null ? 1 : 0),
    cell: ({ row }) =>
      row.original.latitude === null || row.original.longitude === null ? (
        <span className="muted">no position</span>
      ) : (
        <code>
          {row.original.latitude.toFixed(6)}, {row.original.longitude.toFixed(6)}
        </code>
      ),
  },
  {
    id: 'source',
    header: 'From',
    accessorKey: 'source',
    cell: ({ row }) =>
      row.original.source === 'device' ? (
        <Pill reason="The router's own GPS fix, from the polled gps_fix check.">device GPS</Pill>
      ) : row.original.source === 'site' ? (
        <Pill state="stale" reason="The position filed on the site record, not where the device says it is.">
          site record
        </Pill>
      ) : (
        <span className="muted">—</span>
      ),
  },
  {
    id: 'drift',
    header: 'Drift',
    accessorFn: (a) => a.driftM ?? -1,
    meta: { align: 'right' },
    cell: ({ row }) =>
      row.original.driftM === null ? (
        <span className="muted">—</span>
      ) : row.original.drifted ? (
        <Pill state="fault" reason="Far from its filed site: moved, filed wrong, or in somebody's van.">
          {metres(row.original.driftM)}
        </Pill>
      ) : (
        metres(row.original.driftM)
      ),
  },
];

export function MapView(): React.JSX.Element {
  const [data, setData] = useState<MapResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  /*
   * Australia by default, not the fleet. The whole point of a basemap is
   * knowing WHERE something is, and a view cropped to the fleet answers that
   * only for somebody who already knows the coastline they are looking at.
   * Zooming in is one click; realising you are looking at a crop is not.
   */
  const [fit, setFit] = useState<'australia' | 'fleet'>('australia');

  useEffect(() => {
    let live = true;
    api
      .get<MapResponse>('/api/map')
      .then((result) => {
        if (live) setData(result);
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof ApiError ? err.message : String(err));
      });
    return () => {
      live = false;
    };
  }, []);

  const rows = useMemo(() => data?.assets ?? [], [data]);

  if (error !== null) return <Banner tone="fault">{error}</Banner>;
  if (data === null) return <Loading rows={5} cols={5} label="Loading map" />;

  const fleet = fleetBounds(data.assets);
  const bounds = fit === 'fleet' && fleet !== null ? fleet : AU_BOUNDS;
  const projection = projectorFor(bounds);
  const placed = place(data.assets, projection);
  const unplaced = data.assets.length - data.located;

  const summary =
    `${data.located} of ${data.assets.length} located` +
    (unplaced > 0 ? ` · ${unplaced} with no position` : '') +
    (data.drifted > 0 ? ` · ${data.drifted} away from their site` : '') +
    ' — bundled Natural Earth coastline, no network request, good to about a kilometre.';

  return (
    <div className="stack">
      <Section
        summary={summary}
        actions={
          // Two mutually exclusive views of one thing, joined rather than spaced.
          <div className="segmented" role="group" aria-label="Map extent">
            <Button
              variant="ghost"
              size="sm"
              className={fit === 'australia' ? 'on' : undefined}
              aria-pressed={fit === 'australia'}
              onClick={() => setFit('australia')}
            >
              Australia
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={fit === 'fleet' ? 'on' : undefined}
              aria-pressed={fit === 'fleet'}
              disabled={fleet === null}
              onClick={() => setFit('fleet')}
            >
              Fit fleet
            </Button>
          </div>
        }
      >
        {placed.length === 0 ? (
          <Empty
            title="Nothing has a position yet"
            body="Sites carry latitude and longitude, and routers report a GPS fix on the polled gps_fix check."
          />
        ) : (
          <svg
            viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
            className="map-plot"
            role="img"
            aria-label={`${placed.length} assets on a map of Australia`}
          >
            {/*
              Land first, so every marker sits on top of it. One path per ring
              rather than one per state: a state's islands are separate rings and
              joining them would draw a line across the sea between them.
            */}
            <g className="map-land">
              {AU_STATES.map((state) =>
                state.rings.map((ring, i) => (
                  <path key={`${state.name}-${i}`} d={ringPath(ring, projection)}>
                    {i === 0 && <title>{state.name}</title>}
                  </path>
                )),
              )}
            </g>

            {placed.map((a) => (
              <g key={a.id}>
                <circle
                  cx={a.x}
                  cy={a.y}
                  r={a.drifted ? 7 : 5}
                  className={
                    a.drifted
                      ? 'map-marker map-marker-drifted'
                      : a.source === 'device'
                        ? 'map-marker map-marker-device'
                        : 'map-marker map-marker-site'
                  }
                >
                  <title>
                    {`${a.name}${a.code === null ? '' : ` (${a.code})`}\n` +
                      `${a.latitude?.toFixed(6)}, ${a.longitude?.toFixed(6)}\n` +
                      `from ${a.source === 'device' ? 'device GPS' : 'site record'}` +
                      (a.driftM === null ? '' : `\n${metres(a.driftM)} from its site`)}
                  </title>
                </circle>
              </g>
            ))}
          </svg>
        )}
      </Section>

      <Section flush title="Positions" summary="Coordinates on every row — the thing to paste into a real map when the question is metres.">
        <DataTable
          data={rows}
          columns={COLUMNS}
          getRowId={(a) => a.id}
          initialSort={[{ id: 'drift', desc: true }]}
          rowClass={(a) => (a.drifted ? 'row-fault' : undefined)}
          caption="Asset positions"
          empty={<Empty title="No assets in this tenant" />}
        />
      </Section>
    </div>
  );
}
